import * as React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as services from "../../store/service";
import { ICSIDUserStatus } from "../../store/csid/types";
import RenderIcons from "../../Assets/css/_icons/partner_icon/RenderIcons";
import { useTranslation } from "react-i18next";

interface IProps {
    closeButtonText: string;
    onCloseCallback: any;
    openAsDialog: boolean;
    submitButtonText: string;
    onSubmitCallback: any;
    displaySSNInputNumber: boolean;
    csidUserStatus: ICSIDUserStatus;
}

const Last4SSNDigitComponent: React.FunctionComponent<IProps> = (props) => {
    const { t: tLang, i18n: i18nLang } = useTranslation();

    const status = {
        success: "success",
        error: "error",
        apiException: "api-exception",
        apiStatus: "api-status",
    };

    let strCloseButtonText = "Close";
    let strSubmitButtonText = "Submit";
    let blnOpenAsDialog = false;
    let ssnInputFieldType = "password";
    // set default value for props, if not passed
    if (props.closeButtonText != null) {
        strCloseButtonText = props.closeButtonText;
    }
    if (props.submitButtonText != null) {
        strSubmitButtonText = props.submitButtonText;
    }
    if (props.openAsDialog != null) {
        blnOpenAsDialog = props.openAsDialog;
    }
    if (props.displaySSNInputNumber) {
        ssnInputFieldType = "text";
    }
    // use form-submit-hook
    const { register, errors, reset, formState, handleSubmit, clearErrors } = useForm({
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    const { isSubmitting } = formState;
    const [isLoading, setIsLoading] = useState(false);

    // in case of dialog, register event for escape press
    if (blnOpenAsDialog) {
        window.addEventListener("keyup", (evt: any) => {
            evt = evt || window.event;
            var isEscape = false;
            if ("key" in evt) {
                isEscape = evt.key === "Escape" || evt.key === "Esc";
            } else {
                isEscape = evt.keyCode === 27;
            }
            if (isEscape) {
                window.removeEventListener("keyup", () => {});
                onClose("cancel");
            }
        });
    }

    const invokePostSubmitCallback = (status: string, data: any) => {
        if (props.onSubmitCallback != null) {
            props.onSubmitCallback({ status: status, data: data });
        }
    };

    function onChangeHandler(event: any) {
        clearErrors();
    }

    // handle form submit
    const onSubmit = (data: any) => {
        const ssn = data["last-4-ssn"];
        // set the loader into UI;
        setIsLoading(true);
        if (props.csidUserStatus?.csidActivationStatus === "ACTIVATED") {
            services
                .updateUserinCSID(ssn, "")
                .then((data) => {
                    // remove loader from UI
                    setIsLoading(false);
                    if (data["status-code"] === "200") {
                        invokePostSubmitCallback(status.success, { ssn: ssn, detail: data });
                    } else {
                        invokePostSubmitCallback(status.error, { reason: status.apiStatus, detail: data });
                    }
                })
                .catch((error) => {
                    setIsLoading(false);
                    invokePostSubmitCallback(status.error, { reason: status.apiException, detail: error });
                });
        } else {
            // send http request to enroll the user in CSID.
            services
                .enrollUserinCSID(ssn, "")
                .then((data) => {
                    // remove loader from UI
                    setIsLoading(false);
                    if (data["status-code"] === "200") {
                        invokePostSubmitCallback(status.success, { ssn: ssn, detail: data });
                    } else {
                        invokePostSubmitCallback(status.error, { reason: status.apiStatus, detail: data });
                    }
                })
                .catch((error) => {
                    setIsLoading(false);
                    invokePostSubmitCallback(status.error, { reason: status.apiException, detail: error });
                });
        }
    };

    // handle close button click
    // add reason, so that the callback function can identify, if it's cancel (incase of dialog: ESC press) or button click close
    const onClose = (reason?: string) => {
        // reset form
        reset();
        // execute method passed as props for onCloseButtonClick
        if (props.onCloseCallback != null) {
            props.onCloseCallback({ reason: reason == null ? "close" : "cancel" });
        }
    };

    return (
        <React.Fragment>
            <div className={`${isLoading && "loading"}`}>
                <form id="form-last4ssn" onSubmit={handleSubmit(onSubmit)} style={{ padding: "20px" }}>
                    <div className="dialog-header">
                        {props.openAsDialog && (
                            <div className="dialog-icon status-default">
                                <RenderIcons iconName="shield-alt" />
                            </div>
                        )}
                        <h1>{tLang("dashboard.creditScore.last4SSNDigitComp.header")}</h1>
                        <p>{tLang("dashboard.creditScore.last4SSNDigitComp.description")}</p>
                    </div>

                    <div className="dialog-body">
                        <div className={errors["last-4-ssn"] ? "form-row status-error" : "form-row"}>
                            <div className="form-label">
                                <label htmlFor="last-4-ssn-id">
                                    {tLang("dashboard.creditScore.last4SSNDigitComp.label")}{" "}
                                    <span className="required" title="Mandatory">
                                        {" "}
                                        *{" "}
                                    </span>
                                </label>
                            </div>
                            <div className="form-input">
                                <input
                                    ref={register({ required: true, pattern: /^\d{4}$/ })}
                                    type={ssnInputFieldType}
                                    maxLength={4}
                                    name="last-4-ssn"
                                    className="inputbox"
                                    id="last-4-ssn-id"
                                    placeholder="eg. 3456"
                                    onChange={onChangeHandler}
                                />
                            </div>
                            {{ ...errors["last-4-ssn"] }.type === "required" && (
                                <span className="form-error"> {tLang("dashboard.creditScore.last4SSNDigitComp.requiredErrorMessage")}</span>
                            )}
                            {{ ...errors["last-4-ssn"] }.type === "pattern" && (
                                <span className="form-error"> {tLang("dashboard.creditScore.last4SSNDigitComp.invalidErrorMessage")}</span>
                            )}
                        </div>
                    </div>
                    <div className="dialog-footer">
                        <div className="row between-xs">
                            {
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button
                                        type="button"
                                        id="btn-close-last-4-ssn"
                                        className="btn-default btn-link btn-no-padding btn-no-border"
                                        onClick={() => onClose()}
                                    >
                                        <span className="btn-text">{strCloseButtonText}</span>
                                    </button>
                                </div>
                            }
                            <div className="col-xs col-no-gutter flex-grow-null">
                                <button type="submit" className="btn-default btn-primary" disabled={Object.keys(errors).length > 0 || isSubmitting}>
                                    <span className="btn-text">{strSubmitButtonText}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </React.Fragment>
    );
};

export default Last4SSNDigitComponent;
